/**
 * SEOHead — Reusable component for per-page meta tags
 * Uses react-helmet-async to dynamically set title, description, OG, Twitter, canonical.
 *
 * Usage (PT): <SEOHead title="Estudos" description="..." path="/estudos" />
 * Usage (EN): <SEOHead title="Studies" description="..." path="/en/studies" lang="en" alternatePath="/estudos" />
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'VUKZ';
const BASE_URL = 'https://vukz.ai';
const DEFAULT_IMAGE = `${BASE_URL}/vukz-og-image.png`;

const SEOHead = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  type = 'website',
  lang = 'pt',           // 'pt' | 'en'
  alternatePath = null,  // alternate language path for hreflang
}) => {
  const isEN = lang === 'en';
  const defaultDescription = isEN
    ? 'Sports Intelligence Platform with AI. Swimming, athletics and football.'
    : 'Plataforma de Inteligência Esportiva com IA. Natação, atletismo e futebol.';
  const defaultTitle = isEN
    ? `${SITE_NAME} — Sports Intelligence`
    : `${SITE_NAME} — Inteligência Esportiva`;

  const fullTitle = title ? `${title} | ${SITE_NAME}` : defaultTitle;
  const desc = description || defaultDescription;
  const url = `${BASE_URL}${path}`;
  const ogLocale = isEN ? 'en_US' : 'pt_BR';

  // Build alternate URLs for hreflang
  const ptPath = isEN ? alternatePath : path;
  const enPath = isEN ? path : alternatePath;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />

      {/* hreflang — only when both paths are known */}
      {ptPath && <link rel="alternate" hreflang="pt-BR" href={`${BASE_URL}${ptPath}`} />}
      {enPath && <link rel="alternate" hreflang="en" href={`${BASE_URL}${enPath}`} />}
      {enPath && <link rel="alternate" hreflang="x-default" href={`${BASE_URL}${enPath}`} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {/* noindex for internal pages */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
};

export default SEOHead;
